//@ts-nocheck
"use client";

import styles from "./filter-dropdown-list.module.css";
import { GoDotFill } from "react-icons/go";
import { IoClose } from "react-icons/io5";
import { TbLoaderQuarter } from "react-icons/tb";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useState, useEffect, useRef } from "react";

const FilterDropdownList = ({
    item,
    mode = "filter",
    setValue = null,
    autoload = [],
}) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [loadingKey, setLoadingKey] = useState<string | null>(null);
    const [openDetails, setOpenDetails] = useState<string | null>(null);
    const [dropAndGo, setDropAndGo] = useState(null);

    // Store a ref for every <details> element
    const detailsRefs = useRef<Record<string, HTMLDetailsElement | null>>({});

    // Get selected title from searchParams dynamically
    const getSelectedTitle = (keyName) => {
        return searchParams.get(keyName) || null;
    };

    // Clear selection
    const clearSelection = (
        key: string,
        keyName: string,
        title: string
    ) => {
        if (mode === "drop&go") {
            setDropAndGo((prev) =>
                prev === title ? null : title
            );

            setOpenDetails(null);

            setValue(key, null);
        } else {
            setLoadingKey(key);

            const params = new URLSearchParams(
                searchParams.toString()
            );

            params.delete(key);

            router.push(
                `${pathname}?${params.toString()}`,
                { scroll: false }
            );
        }
    };

    // Handle item click
    const handleClick = (
        key: string,
        title: string
    ) => {
        const selectedTitle = getSelectedTitle(key);

        if (selectedTitle === title) {
            clearSelection(key, null, title);
            return;
        }

        if (mode === "drop&go") {
            setDropAndGo(title);
            setValue(key, title);

            return;
        }

        setLoadingKey(key);

        const params = new URLSearchParams(
            searchParams.toString()
        );

        if (params.get(key) === title) {
            params.delete(key);
        } else {
            params.set(key, title);
        }

        router.push(
            `${pathname}?${params.toString()}`,
            { scroll: false }
        );
    };

    // Stop loading when search params change
    useEffect(() => {
        setLoadingKey(null);
    }, [searchParams]);

    // Auto-load first filter
    const hasAutoLoaded = useRef(false);

    useEffect(() => {
        const autoloadList =
            Array.isArray(autoload)
                ? autoload
                : [];

        if (
            !hasAutoLoaded.current &&
            autoloadList.length !== 0
        ) {
            hasAutoLoaded.current = true;

            handleClick(
                autoloadList[0],
                autoloadList[1]
            );
        }
    }, [autoload]);

    /*
     * Force-close any <details> whose React condition
     * says it should no longer be open.
     *
     * This handles the case where:
     * - user selects an option
     * - clicks X to clear it
     * - mouse is still hovering over the dropdown
     * - native <details> remains open even though React state says false
     */
    useEffect(() => {
        Object.entries(detailsRefs.current).forEach(
            ([keyName, details]) => {
                if (!details) return;

                const selectedTitle =
                    getSelectedTitle(keyName);

                const shouldBeOpen =
                    openDetails === keyName &&
                    !selectedTitle &&
                    !dropAndGo;

                if (!shouldBeOpen && details.open) {
                    details.open = false;
                }
            }
        );
    }, [
        openDetails,
        dropAndGo,
        searchParams,
    ]);

    if (!item || typeof item !== "object") return null;

    return (
        <div className={styles["btn-wrapper"]}>
            {Object.entries(item).map(
                ([keyName, values]) => {
                    // The option list may arrive as a non-array (e.g. an API error object).
                    const options =
                        Array.isArray(values)
                            ? values
                            : [];

                    const firstOptionKey =
                        options[0]?.attributes
                            ?.key || keyName;

                    const selectedTitle =
                        getSelectedTitle(keyName);

                    const shouldBeOpen =
                        openDetails === keyName &&
                        !selectedTitle &&
                        !dropAndGo;

                    return (
                        <details
                            key={keyName}
                            ref={(el) => {
                                detailsRefs.current[keyName] =
                                    el;
                            }}
                            open={shouldBeOpen}
                            onMouseEnter={() => {
                                setOpenDetails(keyName);
                            }}
                            onMouseLeave={() => {
                                setOpenDetails(null);

                                // Explicitly close the DOM element
                                const details =
                                    detailsRefs.current[
                                    keyName
                                    ];

                                if (details) {
                                    details.open = false;
                                }
                            }}
                        >
                            <summary
                                onClick={(e) => {
                                    // Prevent native <details>
                                    // click toggling.
                                    e.preventDefault();
                                }}
                            >
                                {(mode === "drop&go"
                                    ? dropAndGo
                                    : selectedTitle) ||
                                    keyName}

                                {loadingKey === keyName ? (
                                    <TbLoaderQuarter
                                        size={18}
                                        color="#ff6d10"
                                        className={
                                            styles.spinner
                                        }
                                    />
                                ) : selectedTitle ||
                                    dropAndGo ? (
                                    <IoClose
                                        size={25}
                                        color="red"
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            clearSelection(
                                                mode ===
                                                    "drop&go"
                                                    ? firstOptionKey
                                                    : keyName,
                                                mode ===
                                                    "drop&go"
                                                    ? keyName
                                                    : null,
                                                mode ===
                                                    "drop&go"
                                                    ? dropAndGo
                                                    : null
                                            );
                                        }}
                                    />
                                ) : (
                                    <GoDotFill
                                        size={15}
                                        color="#ff6d10"
                                    />
                                )}
                            </summary>

                            {!selectedTitle && (
                                <div
                                    className={
                                        styles.list
                                    }
                                >
                                    {options.map(
                                        (itm, i) => {
                                            const attrs =
                                                itm?.attributes ??
                                                {};

                                            const title =
                                                attrs.title != null
                                                    ? String(attrs.title)
                                                    : "";

                                            const optionValue =
                                                String(
                                                    attrs.value ??
                                                    title
                                                );

                                            const key =
                                                String(
                                                    attrs.key ||
                                                    keyName
                                                );

                                            const isSelected =
                                                selectedTitle ===
                                                optionValue;

                                            const isLoading =
                                                isSelected &&
                                                loadingKey ===
                                                key;

                                            return (
                                                <div
                                                    key={i}
                                                    onClick={() =>
                                                        handleClick(
                                                            key,
                                                            optionValue
                                                        )
                                                    }
                                                    className={
                                                        styles[
                                                        "list-item"
                                                        ]
                                                    }
                                                >
                                                    {title}{" "}

                                                    {isLoading ? (
                                                        <TbLoaderQuarter
                                                            size={
                                                                18
                                                            }
                                                            color="#ff6d10"
                                                            className={
                                                                styles.spinner
                                                            }
                                                        />
                                                    ) : isSelected ? (
                                                        <IoClose
                                                            size={
                                                                15
                                                            }
                                                            color="red"
                                                        />
                                                    ) : (
                                                        <GoDotFill
                                                            size={
                                                                15
                                                            }
                                                            color="#ff6d10"
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            )}
                        </details>
                    );
                }
            )}
        </div>
    );
};

export default FilterDropdownList;